import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Alert,
  CircularProgress,
  Chip,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  CloudUpload as UploadIcon,
  ExpandMore as ExpandMoreIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { moodleApi, MoodleSource, ConnectionTestResult } from '../services/moodle-api';
import { rangeosEnvironmentService, RangeOSEnvironment } from '../services/rangeos-environment-api';
import { kubeconfigApi, KubeconfigMetadata, rancherApi, RancherConfig } from '../services/integrations-api';
import { s3BucketsApi, S3BucketConfig } from '../services/s3-buckets-api';

interface MoodleSourceManagerProps {
  open: boolean;
  onClose: () => void;
  editSourceId?: string; // If provided, opens directly to edit this source
}

export const MoodleSourceManager: React.FC<MoodleSourceManagerProps> = ({ open, onClose, editSourceId }) => {
  const [sources, setSources] = useState<MoodleSource[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editingSource, setEditingSource] = useState<MoodleSource | null>(null);
  const [testingConnection, setTestingConnection] = useState<string | null>(null);
  const [connectionResults, setConnectionResults] = useState<Map<string, ConnectionTestResult>>(new Map());
  const [hasOpenedEditDialog, setHasOpenedEditDialog] = useState(false);
  const [rangeosEnvironments, setRangeosEnvironments] = useState<RangeOSEnvironment[]>([]);

  useEffect(() => {
    if (open) {
      loadSources();
      setHasOpenedEditDialog(false); // Reset flag when main dialog opens
    }
  }, [open]);

  // If editSourceId is provided, load sources and then open edit dialog (only once)
  useEffect(() => {
    if (open && editSourceId && sources.length > 0 && !hasOpenedEditDialog) {
      const sourceToEdit = sources.find(s => s.id === editSourceId);
      if (sourceToEdit) {
        setEditingSource(sourceToEdit);
        setAddDialogOpen(true); // Open the edit dialog automatically
        setHasOpenedEditDialog(true); // Mark that we've opened it
      }
    }
  }, [open, editSourceId, sources, hasOpenedEditDialog]);

  // Load RangeOS environments when dialog opens
  useEffect(() => {
    if (addDialogOpen) {
      loadRangeOSEnvironments();
    }
  }, [addDialogOpen]);

  const loadSources = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await moodleApi.listSources();
      setSources(data);
    } catch (err) {
      console.error('Failed to load Moodle sources:', err);
      setError('Failed to load Moodle sources');
    } finally {
      setLoading(false);
    }
  };

  const loadRangeOSEnvironments = async () => {
    try {
      const environments = await rangeosEnvironmentService.getActiveEnvironments();
      setRangeosEnvironments(environments);
    } catch (err) {
      console.error('Failed to load RangeOS environments:', err);
      // Don't set error - this is optional functionality
    }
  };

  const handleTestConnection = async (source: MoodleSource) => {
    setTestingConnection(source.id);
    try {
      const result = await moodleApi.testConnection(source.id);
      setConnectionResults(new Map(connectionResults.set(source.id, result)));
    } catch (err) {
      console.error('Connection test failed:', err);
      setConnectionResults(
        new Map(
          connectionResults.set(source.id, {
            success: false,
            message: err instanceof Error ? err.message : 'Connection test failed',
          })
        )
      );
    } finally {
      setTestingConnection(null);
    }
  };

  const handleDeleteSource = async (id: string) => {
    if (!confirm('Are you sure you want to delete this Moodle source?')) {
      return;
    }

    try {
      await moodleApi.deleteSource(id);
      await loadSources();
    } catch (err) {
      console.error('Failed to delete source:', err);
      setError('Failed to delete source');
    }
  };

  const handleAddSource = () => {
    setEditingSource(null);
    setAddDialogOpen(true);
  };

  const handleEditSource = (source: MoodleSource) => {
    setEditingSource(source);
    setAddDialogOpen(true);
  };

  // If editSourceId is provided, only render the edit dialog, not the sources list
  if (editSourceId) {
    return (
      <>
        {addDialogOpen && (
          <AddEditMoodleSourceDialog
            open={addDialogOpen}
            onClose={() => {
              setAddDialogOpen(false);
              setEditingSource(null);
              onClose(); // Also close parent
            }}
            onSuccess={() => {
              console.log('onSuccess callback called, closing dialog');
              setAddDialogOpen(false);
              setEditingSource(null);
              onClose(); // Close everything
            }}
            editingSource={editingSource}
            rangeosEnvironments={rangeosEnvironments}
          />
        )}
      </>
    );
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Moodle Sources</Typography>
          <Box>
            <IconButton onClick={loadSources} disabled={loading}>
              <RefreshIcon />
            </IconButton>
            <Button
              startIcon={<AddIcon />}
              variant="contained"
              onClick={handleAddSource}
              sx={{ ml: 1 }}
            >
              Add Source
            </Button>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : sources.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center', bgcolor: 'action.hover' }}>
            <Typography color="text.secondary" gutterBottom>
              No Moodle sources configured
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Add a Moodle Kubernetes source to export courses and content directly from your live
              Moodle instances.
            </Typography>
            <Button startIcon={<AddIcon />} variant="contained" onClick={handleAddSource}>
              Add Your First Source
            </Button>
          </Paper>
        ) : (
          <List>
            {sources.map((source) => {
              const connectionResult = connectionResults.get(source.id);
              const isTesting = testingConnection === source.id;

              return (
                <Accordion key={source.id}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box display="flex" alignItems="center" gap={2} width="100%">
                      <Typography variant="h6">{source.name}</Typography>
                      <Chip
                        size="small"
                        label={source.type === 'api' ? 'API' : 'K8s'}
                        color={source.type === 'api' ? 'info' : 'default'}
                        variant="outlined"
                      />
                      {connectionResult && (
                        <Chip
                          size="small"
                          icon={connectionResult.success ? <CheckIcon /> : <ErrorIcon />}
                          label={connectionResult.success ? 'Connected' : 'Failed'}
                          color={connectionResult.success ? 'success' : 'error'}
                        />
                      )}
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box>
                      {source.type === 'api' ? (
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          <strong>Moodle URL:</strong> {source.moodleUrl}
                        </Typography>
                      ) : (
                        <>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            <strong>Namespace:</strong> {source.namespace}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            <strong>Pod:</strong> {source.podName}
                          </Typography>
                        </>
                      )}
                      {source.description && (
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          <strong>Description:</strong> {source.description}
                        </Typography>
                      )}
                      <Typography variant="caption" color="text.secondary" display="block" mt={1}>
                        Added: {new Date(source.createdAt).toLocaleDateString()}
                      </Typography>

                      {connectionResult && !connectionResult.success && (
                        <Alert severity="error" sx={{ mt: 2 }}>
                          {connectionResult.message}
                        </Alert>
                      )}

                      <Box display="flex" gap={1} mt={2} flexWrap="wrap">
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => handleTestConnection(source)}
                          disabled={isTesting}
                          startIcon={isTesting ? <CircularProgress size={16} /> : <CheckIcon />}
                        >
                          {isTesting ? 'Testing...' : 'Test Connection'}
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<EditIcon />}
                          onClick={() => handleEditSource(source)}
                        >
                          Edit
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          startIcon={<DeleteIcon />}
                          onClick={() => handleDeleteSource(source.id)}
                        >
                          Delete
                        </Button>
                      </Box>
                    </Box>
                  </AccordionDetails>
                </Accordion>
              );
            })}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>

      {addDialogOpen && (
        <AddEditMoodleSourceDialog
          open={addDialogOpen}
          onClose={() => {
            setAddDialogOpen(false);
            setEditingSource(null);
          }}
          onSuccess={() => {
            console.log('onSuccess callback called, closing dialog');
            setAddDialogOpen(false);
            setEditingSource(null);
            // If we're editing from Settings (editSourceId provided), close the parent dialog too
            if (editSourceId) {
              onClose();
            } else {
              // Otherwise just reload the sources list
              setTimeout(() => {
                loadSources();
              }, 0);
            }
          }}
          editingSource={editingSource}
          rangeosEnvironments={rangeosEnvironments}
        />
      )}

    </Dialog>
  );
};

interface AddEditMoodleSourceDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  editingSource: MoodleSource | null;
  rangeosEnvironments: RangeOSEnvironment[];
}

const AddEditMoodleSourceDialog: React.FC<AddEditMoodleSourceDialogProps> = ({
  open,
  onClose,
  onSuccess,
  editingSource,
  rangeosEnvironments,
}) => {
  const [connectionType, setConnectionType] = useState<'k8s-exec' | 'api'>(
    editingSource?.type === 'api' ? 'api' : 'k8s-exec'
  );
  const [name, setName] = useState(editingSource?.name || '');
  const [description, setDescription] = useState(editingSource?.description || '');

  // API mode fields
  const [moodleUrl, setMoodleUrl] = useState(editingSource?.moodleUrl || '');
  const [moodleToken, setMoodleToken] = useState(editingSource?.moodleToken || '');
  const [cptSidecarUrl, setCptSidecarUrl] = useState(editingSource?.cptSidecarUrl || '');
  const [cptSidecarApiKey, setCptSidecarApiKey] = useState(editingSource?.cptSidecarApiKey || '');

  // K8s exec mode fields
  const [kubeconfigFile, setKubeconfigFile] = useState<File | null>(null);
  const [kubeconfigMode, setKubeconfigMode] = useState<'upload' | 'select'>(
    editingSource?.kubeconfigId ? 'select' : 'upload'
  );
  const [selectedKubeconfigId, setSelectedKubeconfigId] = useState<string>(editingSource?.kubeconfigId || '');
  const [managedKubeconfigs, setManagedKubeconfigs] = useState<KubeconfigMetadata[]>([]);
  const [rancherInstances, setRancherInstances] = useState<RancherConfig[]>([]);
  const [namespace, setNamespace] = useState(editingSource?.namespace || '');
  const [podName, setPodName] = useState(editingSource?.podName || '');
  
  // AWS Credentials (optional)
  const [awsAccessKeyId, setAwsAccessKeyId] = useState(editingSource?.awsAccessKeyId || '');
  const [awsSecretAccessKey, setAwsSecretAccessKey] = useState(editingSource?.awsSecretAccessKey || '');
  const [awsRegion, setAwsRegion] = useState(editingSource?.awsRegion || '');
  const [awsS3Bucket, setAwsS3Bucket] = useState(editingSource?.awsS3Bucket || '');

  // CMI5/cpt-player Configuration (optional)
  const [cptPlayerNamespace, setCptPlayerNamespace] = useState(editingSource?.cptPlayerNamespace || '');
  const [cptPlayerPodName, setCptPlayerPodName] = useState(editingSource?.cptPlayerPodName || '');

  // RangeOS Environment reference
  const [rangeosEnvironmentId, setRangeosEnvironmentId] = useState(editingSource?.rangeosEnvironmentId || '');
  const [useEnvironment, setUseEnvironment] = useState<boolean>(!!editingSource?.rangeosEnvironmentId);

  // Legacy CMI5 Player Location Configuration (deprecated in favor of rangeosEnvironmentId)
  const [cptPlayerLocationName, setCptPlayerLocationName] = useState(editingSource?.cptPlayerLocationName || 'bylight-demo');
  const [cptPlayerLocationUrl, setCptPlayerLocationUrl] = useState(editingSource?.cptPlayerLocationUrl || 'https://cpt-player.demo-cp.rangeos.engineering');
  const [cptPlayerDevOpsApiUrl, setCptPlayerDevOpsApiUrl] = useState(editingSource?.cptPlayerDevOpsApiUrl || 'https://rangeos-api.demo-cp.rangeos.engineering');
  const [cptPlayerDevOpsGqlUrl, setCptPlayerDevOpsGqlUrl] = useState(editingSource?.cptPlayerDevOpsGqlUrl || 'https://rangeos-graphql.demo-cp.rangeos.engineering/');
  const [cptPlayerDevOpsGqlSubscriptionsUrl, setCptPlayerDevOpsGqlSubscriptionsUrl] = useState(editingSource?.cptPlayerDevOpsGqlSubscriptionsUrl || 'wss://rangeos-graphql.demo-cp.rangeos.engineering');
  const [cptPlayerSlideBackground, setCptPlayerSlideBackground] = useState(editingSource?.cptPlayerSlideBackground || '');
  const [cptPlayerSlideLogo, setCptPlayerSlideLogo] = useState(editingSource?.cptPlayerSlideLogo || '../../../../assets/bylight/RangeOS_Logo_White.png');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [discoveredNamespaces, setDiscoveredNamespaces] = useState<string[]>([]);
  const [discoveredPods, setDiscoveredPods] = useState<Array<{ name: string; status: string }>>([]);
  const [discoveringNamespaces, setDiscoveringNamespaces] = useState(false);
  const [discoveringPods, setDiscoveringPods] = useState(false);

  // CMI5 cpt-player discovery state
  const [discoveredCptPlayerPods, setDiscoveredCptPlayerPods] = useState<Array<{ name: string; status: string }>>([]);
  const [discoveringCptPlayerPods, setDiscoveringCptPlayerPods] = useState(false);
  const [availableS3Buckets, setAvailableS3Buckets] = useState<S3BucketConfig[]>([]);

  // Load managed kubeconfigs and S3 buckets when dialog opens
  useEffect(() => {
    loadManagedKubeconfigs();
    s3BucketsApi.listBuckets().then(setAvailableS3Buckets).catch(() => {});
  }, []);

  const loadManagedKubeconfigs = async () => {
    try {
      const [kubeconfigs, ranchers] = await Promise.all([
        kubeconfigApi.list(),
        rancherApi.getAllInstances().catch(() => []) // Silently fail if no ranchers
      ]);

      setManagedKubeconfigs(kubeconfigs);
      setRancherInstances(ranchers);

      // If there are managed kubeconfigs and no file uploaded, default to select mode
      if (kubeconfigs.length > 0 && !kubeconfigFile) {
        setKubeconfigMode('select');
      }
    } catch (err) {
      console.error('Failed to load managed kubeconfigs:', err);
      // Don't show error - just fall back to upload mode
      setKubeconfigMode('upload');
    }
  };

  // Helper function to format kubeconfig display name with source information
  const formatKubeconfigDisplayName = (kc: KubeconfigMetadata): string => {
    if (kc.source === 'rancher' && kc.rancherInstanceId) {
      const rancherInstance = rancherInstances.find(r => r.id === kc.rancherInstanceId);
      const sourceName = rancherInstance ? rancherInstance.name : 'Rancher';
      return `${kc.name} (${sourceName})`;
    } else if (kc.source === 'eks' && kc.eksRegion) {
      return `${kc.name} (EKS - ${kc.eksRegion})`;
    } else {
      return `${kc.name} (${kc.source})`;
    }
  };

  const handleKubeconfigUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setKubeconfigFile(file);
      setError(null);
      setDiscoveredNamespaces([]);
      setDiscoveredPods([]);
      setNamespace('');
      setPodName('');
      
      // Don't automatically discover namespaces - wait for user to click discover button
      // or provide AWS credentials if needed
    }
  };

  const handleSelectedKubeconfigChange = async (kubeconfigId: string) => {
    setSelectedKubeconfigId(kubeconfigId);
    setError(null);
    setDiscoveredNamespaces([]);
    setDiscoveredPods([]);
    setNamespace('');
    setPodName('');

    if (kubeconfigId) {
      // Auto-discover namespaces from selected kubeconfig
      await handleDiscoverNamespacesFromSelected(kubeconfigId);
    }
  };

  const handleDiscoverNamespacesFromSelected = async (kubeconfigId: string) => {
    setDiscoveringNamespaces(true);
    try {
      const content = await kubeconfigApi.getContent(kubeconfigId);
      const namespaces = await moodleApi.discoverNamespacesFromKubeconfig(
        content,
        awsAccessKeyId || undefined,
        awsSecretAccessKey || undefined,
        awsRegion || undefined
      );

      setDiscoveredNamespaces(namespaces);
      if (namespaces.length > 0) {
        await handleNamespaceChange(namespaces[0]); // Auto-select first namespace and trigger deployment discovery
      }
    } catch (err: any) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to discover namespaces from kubeconfig';
      setError(errorMsg);
    } finally {
      setDiscoveringNamespaces(false);
    }
  };

  const handleDiscoverNamespaces = async () => {
    if (kubeconfigMode === 'select' && selectedKubeconfigId) {
      await handleDiscoverNamespacesFromSelected(selectedKubeconfigId);
      return;
    }

    if (!kubeconfigFile) {
      setError('Please upload a kubeconfig file first');
      return;
    }

    // Discover namespaces from the uploaded kubeconfig
    try {
      setDiscoveringNamespaces(true);
      const content = await kubeconfigFile.text();
      const namespaces = await moodleApi.discoverNamespacesFromKubeconfig(
        content,
        awsAccessKeyId || undefined,
        awsSecretAccessKey || undefined,
        awsRegion || undefined
      );
      setDiscoveredNamespaces(namespaces);
      
      // Auto-select first namespace if only one exists
      if (namespaces.length === 1) {
        setNamespace(namespaces[0]);
      }
    } catch (err) {
      console.error('Failed to discover namespaces:', err);
      const errorMsg = err instanceof Error ? err.message : 'Failed to discover namespaces from kubeconfig';
      
      // Extract user-friendly message from error
      let displayError = errorMsg;
      if (errorMsg.includes('AWS IAM') || errorMsg.includes('AssumeRole')) {
        displayError = 'AWS Authentication Error: ' + errorMsg;
      } else if (errorMsg.includes('Connection failed')) {
        displayError = 'Network Error: ' + errorMsg;
      }
      
      setError(displayError);
    } finally {
      setDiscoveringNamespaces(false);
    }
  };

  const handleNamespaceChange = async (newNamespace: string) => {
    setNamespace(newNamespace);
    setPodName('');
    setDiscoveredPods([]);

    // Discover pods for the selected namespace
    if (newNamespace) {
      if (editingSource) {
        // Editing existing source - use source ID
        await handleDiscoverPodsForSource(editingSource.id, newNamespace);
      } else if (kubeconfigFile) {
        // Creating new source - use kubeconfig content
        await handleDiscoverPodsFromKubeconfig(newNamespace);
      } else if (selectedKubeconfigId) {
        // Creating new source from managed kubeconfig - fetch content and discover
        await handleDiscoverPodsFromSelectedKubeconfig(newNamespace);
      }
    }
  };

  const handleDiscoverPodsFromKubeconfig = async (ns: string) => {
    if (!kubeconfigFile) return;

    setDiscoveringPods(true);
    try {
      const content = await kubeconfigFile.text();
      // Discover deployments instead of pods for stable names
      const deploymentList = await moodleApi.discoverDeploymentsFromKubeconfig(
        content,
        ns,
        awsAccessKeyId || undefined,
        awsSecretAccessKey || undefined,
        awsRegion || undefined
      );
      // Convert deployments to pod-like format for UI compatibility
      const podList = deploymentList.map(d => ({
        name: `deployment:${d.name}`,
        status: `${d.availableReplicas}/${d.replicas} replicas`
      }));
      setDiscoveredPods(podList);

      // Auto-select first deployment if only one exists
      if (podList.length === 1) {
        setPodName(podList[0].name);
      }
    } catch (err) {
      console.error('Failed to discover Moodle deployments:', err);
      const errorMsg = err instanceof Error ? err.message : 'Failed to discover Moodle deployments';
      setError(errorMsg);
    } finally {
      setDiscoveringPods(false);
    }
  };

  const handleDiscoverPodsFromSelectedKubeconfig = async (ns: string) => {
    if (!selectedKubeconfigId) return;

    setDiscoveringPods(true);
    try {
      const content = await kubeconfigApi.getContent(selectedKubeconfigId);
      // Discover deployments instead of pods for stable names
      const deploymentList = await moodleApi.discoverDeploymentsFromKubeconfig(
        content,
        ns,
        awsAccessKeyId || undefined,
        awsSecretAccessKey || undefined,
        awsRegion || undefined
      );
      // Convert deployments to pod-like format for UI compatibility
      const podList = deploymentList.map(d => ({
        name: `deployment:${d.name}`,
        status: `${d.availableReplicas}/${d.replicas} replicas`
      }));
      setDiscoveredPods(podList);

      // Auto-select first deployment if only one exists
      if (podList.length === 1) {
        setPodName(podList[0].name);
      }
    } catch (err) {
      console.error('Failed to discover Moodle deployments from selected kubeconfig:', err);
      const errorMsg = err instanceof Error ? err.message : 'Failed to discover Moodle deployments';
      setError(errorMsg);
    } finally {
      setDiscoveringPods(false);
    }
  };

  const handleDiscoverPodsForSource = async (sourceId: string, ns: string) => {
    setDiscoveringPods(true);
    try {
      // Discover deployments instead of pods for stable names
      const deploymentList = await moodleApi.discoverDeployments(sourceId, ns);
      // Convert deployments to pod-like format for UI compatibility
      const podList = deploymentList.map(d => ({
        name: `deployment:${d.name}`,
        status: `${d.availableReplicas}/${d.replicas} replicas`
      }));
      setDiscoveredPods(podList);

      // Auto-select first deployment if only one exists
      if (podList.length === 1) {
        setPodName(podList[0].name);
      }
    } catch (err) {
      console.error('Failed to discover Moodle deployments:', err);
      setError('Failed to discover Moodle deployments in namespace');
    } finally {
      setDiscoveringPods(false);
    }
  };

  const handleDiscoverPods = async () => {
    if (!editingSource?.id || !namespace) return;
    await handleDiscoverPodsForSource(editingSource.id, namespace);
  };

  const handleDiscoverNamespacesForSource = async () => {
    if (!editingSource?.id) return;

    setDiscoveringNamespaces(true);
    try {
      const namespaces = await moodleApi.discoverNamespaces(editingSource.id);
      setDiscoveredNamespaces(namespaces);

      // Auto-select first namespace if only one exists
      if (namespaces.length === 1) {
        setNamespace(namespaces[0]);
      }
    } catch (err) {
      console.error('Failed to discover namespaces:', err);
      setError('Failed to discover namespaces');
    } finally {
      setDiscoveringNamespaces(false);
    }
  };

  // CMI5 cpt-player discovery handlers
  const handleCptPlayerNamespaceChange = async (newNamespace: string) => {
    setCptPlayerNamespace(newNamespace);
    setCptPlayerPodName('');
    setDiscoveredCptPlayerPods([]);

    // Discover pods for the selected cpt-player namespace
    if (newNamespace) {
      if (editingSource) {
        await handleDiscoverCptPlayerPodsForSource(editingSource.id, newNamespace);
      } else if (kubeconfigFile) {
        await handleDiscoverCptPlayerPodsFromKubeconfig(newNamespace);
      } else if (selectedKubeconfigId) {
        // Creating new source from managed kubeconfig - fetch content and discover
        await handleDiscoverCptPlayerPodsFromSelectedKubeconfig(newNamespace);
      }
    }
  };

  const handleDiscoverCptPlayerPodsFromKubeconfig = async (ns: string) => {
    if (!kubeconfigFile) return;

    setDiscoveringCptPlayerPods(true);
    try {
      const content = await kubeconfigFile.text();
      // Discover deployments instead of pods for stable names
      const deploymentList = await moodleApi.discoverDeploymentsFromKubeconfig(
        content,
        ns,
        awsAccessKeyId || undefined,
        awsSecretAccessKey || undefined,
        awsRegion || undefined
      );
      // Convert deployments to pod-like format for UI compatibility
      const podList = deploymentList.map(d => ({
        name: `deployment:${d.name}`,
        status: `${d.availableReplicas}/${d.replicas} replicas`
      }));
      setDiscoveredCptPlayerPods(podList);

      // Auto-select first deployment if only one exists
      if (podList.length === 1) {
        setCptPlayerPodName(podList[0].name);
      }
    } catch (err) {
      console.error('Failed to discover cpt-player deployments:', err);
      const errorMsg = err instanceof Error ? err.message : 'Failed to discover cpt-player deployments';
      setError(errorMsg);
    } finally {
      setDiscoveringCptPlayerPods(false);
    }
  };

  const handleDiscoverCptPlayerPodsFromSelectedKubeconfig = async (ns: string) => {
    if (!selectedKubeconfigId) return;

    setDiscoveringCptPlayerPods(true);
    try {
      const content = await kubeconfigApi.getContent(selectedKubeconfigId);
      // Discover deployments instead of pods for stable names
      const deploymentList = await moodleApi.discoverDeploymentsFromKubeconfig(
        content,
        ns,
        awsAccessKeyId || undefined,
        awsSecretAccessKey || undefined,
        awsRegion || undefined
      );
      // Convert deployments to pod-like format for UI compatibility
      const podList = deploymentList.map(d => ({
        name: `deployment:${d.name}`,
        status: `${d.availableReplicas}/${d.replicas} replicas`
      }));
      setDiscoveredCptPlayerPods(podList);

      // Auto-select first deployment if only one exists
      if (podList.length === 1) {
        setCptPlayerPodName(podList[0].name);
      }
    } catch (err) {
      console.error('Failed to discover cpt-player deployments from selected kubeconfig:', err);
      const errorMsg = err instanceof Error ? err.message : 'Failed to discover cpt-player deployments';
      setError(errorMsg);
    } finally {
      setDiscoveringCptPlayerPods(false);
    }
  };

  const handleDiscoverCptPlayerPodsForSource = async (sourceId: string, ns: string) => {
    setDiscoveringCptPlayerPods(true);
    try {
      // Discover deployments instead of pods for stable names
      const deploymentList = await moodleApi.discoverDeployments(sourceId, ns);
      // Convert deployments to pod-like format for UI compatibility
      const podList = deploymentList.map(d => ({
        name: `deployment:${d.name}`,
        status: `${d.availableReplicas}/${d.replicas} replicas`
      }));
      setDiscoveredCptPlayerPods(podList);

      // Auto-select first deployment if only one exists
      if (podList.length === 1) {
        setCptPlayerPodName(podList[0].name);
      }
    } catch (err) {
      console.error('Failed to discover cpt-player deployments:', err);
      setError('Failed to discover cpt-player deployments in namespace');
    } finally {
      setDiscoveringCptPlayerPods(false);
    }
  };

  const handleDiscoverCptPlayerPods = async () => {
    if (!editingSource?.id || !cptPlayerNamespace) return;
    await handleDiscoverCptPlayerPodsForSource(editingSource.id, cptPlayerNamespace);
  };

  const handleSubmit = async () => {
    if (!name.trim()) {
      setError('Name is required');
      return;
    }

    if (connectionType === 'api') {
      // API mode validation
      if (!moodleUrl.trim()) {
        setError('Moodle URL is required for API mode');
        return;
      }
      if (!moodleToken.trim()) {
        setError('Moodle Token is required for API mode');
        return;
      }
    } else {
      // K8s exec mode validation
      if (!editingSource) {
        if (kubeconfigMode === 'select' && !selectedKubeconfigId) {
          setError('Please select a kubeconfig');
          return;
        } else if (kubeconfigMode === 'upload' && !kubeconfigFile) {
          setError('Please upload a kubeconfig file');
          return;
        }
      }
      if (!namespace.trim()) {
        setError('Namespace is required');
        return;
      }
      if (!podName.trim()) {
        setError('Pod name is required');
        return;
      }
    }

    setLoading(true);
    setError(null);

    try {
      if (editingSource) {
        // Update existing source
        const updates: Partial<Omit<MoodleSource, 'id' | 'createdAt' | 'updatedAt'>> = {
          name: name.trim(),
          type: connectionType,
          description: description.trim() || undefined,
          // API mode fields
          moodleUrl: connectionType === 'api' ? moodleUrl.trim() : undefined,
          moodleToken: connectionType === 'api' && moodleToken.trim() && !moodleToken.startsWith('****')
            ? moodleToken.trim() : undefined,
          cptSidecarUrl: connectionType === 'api' ? cptSidecarUrl.trim() || undefined : undefined,
          cptSidecarApiKey: connectionType === 'api' && cptSidecarApiKey.trim() && !cptSidecarApiKey.startsWith('****')
            ? cptSidecarApiKey.trim() : undefined,
          // K8s exec mode fields
          namespace: connectionType === 'k8s-exec' ? namespace.trim() : undefined,
          podName: connectionType === 'k8s-exec' ? podName.trim() : undefined,
          // Shared fields
          awsAccessKeyId: awsAccessKeyId.trim() || undefined,
          awsSecretAccessKey: awsSecretAccessKey.trim() || undefined,
          awsRegion: awsRegion.trim() || undefined,
          awsS3Bucket: awsS3Bucket.trim() || undefined,
          cptPlayerNamespace: connectionType === 'k8s-exec' ? cptPlayerNamespace.trim() || undefined : undefined,
          cptPlayerPodName: connectionType === 'k8s-exec' ? cptPlayerPodName.trim() || undefined : undefined,
          rangeosEnvironmentId: useEnvironment && rangeosEnvironmentId ? rangeosEnvironmentId : undefined,
          cptPlayerLocationName: !useEnvironment ? cptPlayerLocationName.trim() || undefined : undefined,
          cptPlayerLocationUrl: !useEnvironment ? cptPlayerLocationUrl.trim() || undefined : undefined,
          cptPlayerDevOpsApiUrl: !useEnvironment ? cptPlayerDevOpsApiUrl.trim() || undefined : undefined,
          cptPlayerDevOpsGqlUrl: !useEnvironment ? cptPlayerDevOpsGqlUrl.trim() || undefined : undefined,
          cptPlayerDevOpsGqlSubscriptionsUrl: !useEnvironment ? cptPlayerDevOpsGqlSubscriptionsUrl.trim() || undefined : undefined,
          cptPlayerSlideBackground: cptPlayerSlideBackground.trim() || undefined,
          cptPlayerSlideLogo: cptPlayerSlideLogo.trim() || undefined,
        };
        await moodleApi.updateSource(editingSource.id, updates);
      } else {
        // Create new source via params object
        let kubeconfigContent: string | undefined;
        if (connectionType === 'k8s-exec') {
          if (kubeconfigMode === 'select' && selectedKubeconfigId) {
            kubeconfigContent = await kubeconfigApi.getContent(selectedKubeconfigId);
          } else if (kubeconfigFile) {
            kubeconfigContent = await kubeconfigFile.text();
          } else {
            throw new Error('No kubeconfig provided');
          }
        }

        await moodleApi.createSourceFromParams({
          type: connectionType,
          name: name.trim(),
          description: description.trim() || undefined,
          // API mode fields
          moodleUrl: connectionType === 'api' ? moodleUrl.trim() : undefined,
          moodleToken: connectionType === 'api' ? moodleToken.trim() : undefined,
          cptSidecarUrl: connectionType === 'api' ? cptSidecarUrl.trim() || undefined : undefined,
          cptSidecarApiKey: connectionType === 'api' ? cptSidecarApiKey.trim() || undefined : undefined,
          // K8s exec mode fields
          kubeconfigContent,
          namespace: connectionType === 'k8s-exec' ? namespace.trim() : undefined,
          podName: connectionType === 'k8s-exec' ? podName.trim() : undefined,
          // Shared fields
          awsAccessKeyId: awsAccessKeyId.trim() || undefined,
          awsSecretAccessKey: awsSecretAccessKey.trim() || undefined,
          awsRegion: awsRegion.trim() || undefined,
          awsS3Bucket: awsS3Bucket.trim() || undefined,
          cptPlayerNamespace: connectionType === 'k8s-exec' ? cptPlayerNamespace.trim() || undefined : undefined,
          cptPlayerPodName: connectionType === 'k8s-exec' ? cptPlayerPodName.trim() || undefined : undefined,
          rangeosEnvironmentId: useEnvironment && rangeosEnvironmentId ? rangeosEnvironmentId : undefined,
          cptPlayerLocationName: !useEnvironment ? cptPlayerLocationName.trim() || undefined : undefined,
          cptPlayerLocationUrl: !useEnvironment ? cptPlayerLocationUrl.trim() || undefined : undefined,
          cptPlayerDevOpsApiUrl: !useEnvironment ? cptPlayerDevOpsApiUrl.trim() || undefined : undefined,
          cptPlayerDevOpsGqlUrl: !useEnvironment ? cptPlayerDevOpsGqlUrl.trim() || undefined : undefined,
          cptPlayerDevOpsGqlSubscriptionsUrl: !useEnvironment ? cptPlayerDevOpsGqlSubscriptionsUrl.trim() || undefined : undefined,
          cptPlayerSlideBackground: cptPlayerSlideBackground.trim() || undefined,
          cptPlayerSlideLogo: cptPlayerSlideLogo.trim() || undefined,
        });
      }

      console.log('Source saved successfully, calling onSuccess() and onClose()');
      onSuccess();
      onClose();
    } catch (err) {
      console.error('Failed to save source:', err);
      setError(err instanceof Error ? err.message : 'Failed to save source');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{editingSource ? 'Edit' : 'Add'} Moodle Source</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <TextField
          autoFocus
          margin="dense"
          label="Name"
          fullWidth
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
        />

        <TextField
          margin="dense"
          label="Description (optional)"
          fullWidth
          multiline
          rows={2}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />

        {/* Connection Type Toggle */}
        <Box mt={2} mb={2}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Connection Type
          </Typography>
          <Box display="flex" gap={1}>
            <Button
              variant={connectionType === 'k8s-exec' ? 'contained' : 'outlined'}
              onClick={() => setConnectionType('k8s-exec')}
              size="small"
            >
              K8s Exec
            </Button>
            <Button
              variant={connectionType === 'api' ? 'contained' : 'outlined'}
              onClick={() => setConnectionType('api')}
              size="small"
            >
              API Mode
            </Button>
          </Box>
        </Box>

        {connectionType === 'api' ? (
          /* API Mode Fields */
          <Box>
            <TextField
              margin="dense"
              label="Moodle URL"
              fullWidth
              value={moodleUrl}
              onChange={(e) => setMoodleUrl(e.target.value)}
              required
              placeholder="https://moodle.example.com"
              helperText="The base URL of your Moodle instance (must have s3contentmanager plugin installed)"
            />

            <TextField
              margin="dense"
              label="Moodle Web Service Token"
              fullWidth
              type="password"
              value={moodleToken}
              onChange={(e) => setMoodleToken(e.target.value)}
              required
              helperText="Token for the s3contentmanager web service"
            />
          </Box>
        ) : (
          /* K8s Exec Mode Fields */
          <>
            <Box mt={2}>
              {/* Show current kubeconfig when editing */}
              {editingSource && selectedKubeconfigId && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  <strong>Current Kubeconfig:</strong>{' '}
                  {(() => {
                    const kc = managedKubeconfigs.find((kc) => kc.id === selectedKubeconfigId);
                    return kc ? formatKubeconfigDisplayName(kc) : selectedKubeconfigId;
                  })()}
                  {' '}(can be changed below)
                </Alert>
              )}

              {/* Kubeconfig Mode Selection */}
              <Box display="flex" gap={1} mb={2}>
                <Button
                  variant={kubeconfigMode === 'select' ? 'contained' : 'outlined'}
                  onClick={() => setKubeconfigMode('select')}
                  size="small"
                  disabled={managedKubeconfigs.length === 0}
                >
                  Select from Managed ({managedKubeconfigs.length})
                </Button>
                <Button
                  variant={kubeconfigMode === 'upload' ? 'contained' : 'outlined'}
                  onClick={() => setKubeconfigMode('upload')}
                  size="small"
                >
                  Upload File
                </Button>
              </Box>

              {/* Select from Managed Kubeconfigs */}
              {kubeconfigMode === 'select' && managedKubeconfigs.length > 0 && (
                <FormControl fullWidth margin="dense">
                  <InputLabel>Select Kubeconfig</InputLabel>
                  <Select
                    value={selectedKubeconfigId}
                    onChange={(e) => handleSelectedKubeconfigChange(e.target.value)}
                    label="Select Kubeconfig"
                  >
                    {managedKubeconfigs.map((kc) => (
                      <MenuItem key={kc.id} value={kc.id}>
                        {formatKubeconfigDisplayName(kc)}
                      </MenuItem>
                    ))}
                  </Select>
                  <Typography variant="caption" color="text.secondary" display="block" mt={1}>
                    Select a kubeconfig from Rancher or manually uploaded configs
                  </Typography>
                </FormControl>
              )}

              {/* Upload Kubeconfig File */}
              {kubeconfigMode === 'upload' && (
                <>
                  <Button
                    variant="outlined"
                    component="label"
                    startIcon={<UploadIcon />}
                    fullWidth
                  >
                    {kubeconfigFile ? kubeconfigFile.name : 'Upload Kubeconfig File'}
                    <input type="file" hidden accept=".yaml,.yml,.conf,.config" onChange={handleKubeconfigUpload} />
                  </Button>
                  <Typography variant="caption" color="text.secondary" display="block" mt={1}>
                    Upload your Kubernetes config file (kubeconfig)
                    {editingSource && ' (will update the kubeconfig for this source)'}
                  </Typography>

                  {kubeconfigFile && !editingSource && (
                    <Button
                      variant="contained"
                      onClick={handleDiscoverNamespaces}
                      fullWidth
                      disabled={discoveringNamespaces}
                      sx={{ mt: 2 }}
                    >
                      {discoveringNamespaces ? (
                        <>
                          <CircularProgress size={16} sx={{ mr: 1 }} />
                          Discovering namespaces...
                        </>
                      ) : (
                        'Discover Namespaces'
                      )}
                    </Button>
                  )}
                </>
              )}
            </Box>

            {/* Namespace selection */}
            {discoveredNamespaces.length > 0 ? (
              <FormControl fullWidth margin="dense" required>
                <InputLabel>Namespace</InputLabel>
                <Select
                  value={namespace}
                  onChange={(e) => handleNamespaceChange(e.target.value)}
                  label="Namespace"
                >
                  {discoveredNamespaces.map((ns) => (
                    <MenuItem key={ns} value={ns}>
                      {ns}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : (
              <TextField
                margin="dense"
                label="Namespace"
                fullWidth
                value={namespace}
                onChange={(e) => handleNamespaceChange(e.target.value)}
                required
                helperText="The Kubernetes namespace where your Moodle pod is running"
              />
            )}

            {/* Pod/Deployment selection */}
            {discoveredPods.length > 0 ? (
              <FormControl fullWidth margin="dense" required>
                <InputLabel>Moodle Deployment</InputLabel>
                <Select
                  value={podName}
                  onChange={(e) => setPodName(e.target.value)}
                  label="Moodle Deployment"
                >
                  {discoveredPods.map((pod) => (
                    <MenuItem key={pod.name} value={pod.name}>
                      {pod.name.replace('deployment:', '')} ({pod.status})
                    </MenuItem>
                  ))}
                </Select>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1.5 }}>
                  Using deployment names ensures stable configuration across pod restarts
                </Typography>
              </FormControl>
            ) : (
              <TextField
                margin="dense"
                label="Moodle Deployment or Pod Name"
                fullWidth
                value={podName}
                onChange={(e) => setPodName(e.target.value)}
                required
                placeholder="e.g., deployment:moodle or moodle-xyz"
                helperText="Prefix with 'deployment:' for stable names (recommended) or use direct pod name"
              />
            )}

            {/* For editing existing sources, show discover buttons */}
            {editingSource && (
              <Box mt={2} display="flex" gap={1} flexWrap="wrap">
                {!discoveredNamespaces.length && (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={handleDiscoverNamespacesForSource}
                    disabled={discoveringNamespaces}
                    startIcon={discoveringNamespaces ? <CircularProgress size={16} /> : <RefreshIcon />}
                  >
                    Discover Namespaces
                  </Button>
                )}
                {namespace && !discoveredPods.length && (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={handleDiscoverPods}
                    disabled={discoveringPods}
                    startIcon={discoveringPods ? <CircularProgress size={16} /> : <RefreshIcon />}
                  >
                    Discover Moodle Deployments in Namespace
                  </Button>
                )}
              </Box>
            )}
          </>
        )}

        {/* AWS Credentials Section (K8s exec mode only - needed for EKS auth) */}
        {connectionType === 'k8s-exec' && (
        <Box mt={3}>
          <Accordion defaultExpanded={false} disableGutters elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                backgroundColor: 'action.hover',
                '&:hover': { backgroundColor: 'action.selected' }
              }}
            >
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  AWS Credentials (Optional)
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Override global credentials for this source
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="caption" color="text.secondary" display="block" mb={2}>
                If this cluster requires different AWS credentials, provide them here. Otherwise, global credentials will be used.
              </Typography>

              <TextField
                margin="dense"
                label="AWS Access Key ID"
                fullWidth
                value={awsAccessKeyId}
                onChange={(e) => setAwsAccessKeyId(e.target.value)}
                placeholder="Leave blank to use global credentials"
              />

              <TextField
                margin="dense"
                label="AWS Secret Access Key"
                fullWidth
                type="password"
                value={awsSecretAccessKey}
                onChange={(e) => setAwsSecretAccessKey(e.target.value)}
                placeholder="Leave blank to use global credentials"
              />

              <TextField
                margin="dense"
                label="AWS Region"
                fullWidth
                value={awsRegion}
                onChange={(e) => setAwsRegion(e.target.value)}
                placeholder="e.g., us-east-1"
              />

              <FormControl fullWidth margin="dense">
                <InputLabel>S3 Bucket (for CMI5 scenario files)</InputLabel>
                <Select
                  value={awsS3Bucket}
                  label="S3 Bucket (for CMI5 scenario files)"
                  onChange={(e) => setAwsS3Bucket(e.target.value as string)}
                >
                  <MenuItem value="">
                    <em>None (use global bucket)</em>
                  </MenuItem>
                  {availableS3Buckets.map((b) => (
                    <MenuItem key={b.id} value={b.name}>
                      {b.name}
                      {b.description ? ` — ${b.description}` : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </AccordionDetails>
          </Accordion>
        </Box>
        )}

        {/* CMI5/cpt-player Configuration Section */}
        <Box mt={2}>
          <Accordion defaultExpanded={false} disableGutters elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                backgroundColor: 'action.hover',
                '&:hover': { backgroundColor: 'action.selected' }
              }}
            >
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  CMI5/cpt-player Configuration (Optional)
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Required for exporting/importing CMI5 learning content
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="caption" color="text.secondary" display="block" mb={2}>
                {connectionType === 'api'
                  ? 'Provide the cpt-player sidecar URL and API key for CMI5 content operations.'
                  : 'If this Moodle instance uses CMI5 activities with cpt-player, provide the cpt-player pod details.'}
              </Typography>

              {connectionType === 'api' ? (
                /* API mode: sidecar URL + API key */
                <>
                  <TextField
                    margin="dense"
                    label="CPT-Player Sidecar URL"
                    fullWidth
                    value={cptSidecarUrl}
                    onChange={(e) => setCptSidecarUrl(e.target.value)}
                    placeholder="https://cpt-sidecar.internal:3399"
                    helperText="URL of the cpt-player sidecar service"
                  />
                  <TextField
                    margin="dense"
                    label="CPT-Player Sidecar API Key"
                    fullWidth
                    type="password"
                    value={cptSidecarApiKey}
                    onChange={(e) => setCptSidecarApiKey(e.target.value)}
                    helperText="API key for sidecar authentication"
                  />
                </>
              ) : (
                /* K8s exec mode: namespace + pod discovery */
                <>
                  {/* cpt-player Namespace selection */}
                  {discoveredNamespaces.length > 0 ? (
                    <FormControl fullWidth margin="dense">
                      <InputLabel>cpt-player Namespace</InputLabel>
                      <Select
                        value={cptPlayerNamespace}
                        onChange={(e) => handleCptPlayerNamespaceChange(e.target.value)}
                        label="cpt-player Namespace"
                      >
                        {discoveredNamespaces.map((ns) => (
                          <MenuItem key={ns} value={ns}>
                            {ns}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  ) : (
                    <TextField
                      margin="dense"
                      label="cpt-player Namespace"
                      fullWidth
                      value={cptPlayerNamespace}
                      onChange={(e) => handleCptPlayerNamespaceChange(e.target.value)}
                      placeholder="e.g., cpt-player"
                      helperText="The Kubernetes namespace where the cpt-player pod is running"
                    />
                  )}

                  {/* cpt-player Deployment/Pod selection */}
                  {discoveredCptPlayerPods.length > 0 && (
                    <FormControl fullWidth margin="dense">
                      <InputLabel>cpt-player Deployment</InputLabel>
                      <Select
                        value={cptPlayerPodName}
                        onChange={(e) => setCptPlayerPodName(e.target.value)}
                        label="cpt-player Deployment"
                      >
                        {discoveredCptPlayerPods.map((pod) => (
                          <MenuItem key={pod.name} value={pod.name}>
                            {pod.name.replace('deployment:', '')} ({pod.status})
                          </MenuItem>
                        ))}
                      </Select>
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1.5 }}>
                        Using deployment names ensures stable configuration across pod restarts
                      </Typography>
                    </FormControl>
                  )}
                </>
              )}

              <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>
                RangeOS Environment
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                Required for scenario export and CMI5 player location configuration during import
              </Typography>

              <FormControl fullWidth margin="dense">
                <InputLabel>Configuration Method</InputLabel>
                <Select
                  value={useEnvironment ? 'environment' : 'manual'}
                  label="Configuration Method"
                  onChange={(e) => setUseEnvironment(e.target.value === 'environment')}
                >
                  <MenuItem value="environment">Use RangeOS Environment</MenuItem>
                  <MenuItem value="manual">Manual Configuration (Legacy)</MenuItem>
                </Select>
              </FormControl>

              {useEnvironment ? (
                <>
                  <FormControl fullWidth margin="dense">
                    <InputLabel>RangeOS Environment</InputLabel>
                    <Select
                      value={rangeosEnvironmentId}
                      label="RangeOS Environment"
                      onChange={(e) => setRangeosEnvironmentId(e.target.value)}
                    >
                      {rangeosEnvironments.map((env) => (
                        <MenuItem key={env.id} value={env.id}>
                          {env.name} - {env.devOpsApiUrl}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {rangeosEnvironments.length === 0 && (
                    <Typography variant="caption" color="warning.main" sx={{ mt: 1, display: 'block' }}>
                      No RangeOS environments configured. Please add one in Settings.
                    </Typography>
                  )}
                </>
              ) : (
                <>
                  <TextField
                    margin="dense"
                    label="Location Name"
                    fullWidth
                    value={cptPlayerLocationName}
                    onChange={(e) => setCptPlayerLocationName(e.target.value)}
                    placeholder="bylight-demo"
                  />

                  <TextField
                    margin="dense"
                    label="Player URL"
                    fullWidth
                    value={cptPlayerLocationUrl}
                    onChange={(e) => setCptPlayerLocationUrl(e.target.value)}
                    placeholder="https://cpt-player.demo-cp.rangeos.engineering"
                  />

                  <TextField
                    margin="dense"
                    label="DevOps API URL"
                    fullWidth
                    value={cptPlayerDevOpsApiUrl}
                    onChange={(e) => setCptPlayerDevOpsApiUrl(e.target.value)}
                    placeholder="https://rangeos-api.demo-cp.rangeos.engineering"
                  />

                  <TextField
                    margin="dense"
                    label="DevOps GraphQL URL"
                    fullWidth
                    value={cptPlayerDevOpsGqlUrl}
                    onChange={(e) => setCptPlayerDevOpsGqlUrl(e.target.value)}
                    placeholder="https://rangeos-graphql.demo-cp.rangeos.engineering/"
                  />

                  <TextField
                    margin="dense"
                    label="DevOps GraphQL Subscriptions URL"
                    fullWidth
                    value={cptPlayerDevOpsGqlSubscriptionsUrl}
                    onChange={(e) => setCptPlayerDevOpsGqlSubscriptionsUrl(e.target.value)}
                    placeholder="wss://rangeos-graphql.demo-cp.rangeos.engineering"
                  />
                </>
              )}

              {/* Slide config only relevant when cpt-player is configured */}
              {(connectionType === 'api' ? (cptSidecarUrl) : (cptPlayerNamespace || discoveredCptPlayerPods.length > 0)) && (
                <>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontStyle: 'italic', mt: 2, mb: 1 }}>
                    Note: Tenant ID is automatically detected from cpt-player plugin settings in the Moodle database
                  </Typography>

                  <TextField
                    margin="dense"
                    label="Slide Background (optional)"
                    fullWidth
                    value={cptPlayerSlideBackground}
                    onChange={(e) => setCptPlayerSlideBackground(e.target.value)}
                    placeholder=""
                  />

                  <TextField
                    margin="dense"
                    label="Slide Logo"
                    fullWidth
                    value={cptPlayerSlideLogo}
                    onChange={(e) => setCptPlayerSlideLogo(e.target.value)}
                    placeholder="../../../../assets/bylight/RangeOS_Logo_White.png"
                  />
                </>
              )}

              {connectionType === 'k8s-exec' && (
                <>
                  {!discoveredCptPlayerPods.length && (
                    <TextField
                      margin="dense"
                      label="cpt-player Deployment or Pod Name"
                      fullWidth
                      value={cptPlayerPodName}
                      onChange={(e) => setCptPlayerPodName(e.target.value)}
                      placeholder="e.g., deployment:cpt-player or cpt-player-xyz"
                      helperText="Prefix with 'deployment:' for stable names (recommended) or use direct pod name"
                    />
                  )}

                  {/* For editing existing sources, show discover deployments button */}
                  {editingSource && cptPlayerNamespace && !discoveredCptPlayerPods.length && (
                    <Box mt={2}>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={handleDiscoverCptPlayerPods}
                        disabled={discoveringCptPlayerPods}
                        startIcon={discoveringCptPlayerPods ? <CircularProgress size={16} /> : <RefreshIcon />}
                      >
                        Discover cpt-player Deployments in Namespace
                      </Button>
                    </Box>
                  )}
                </>
              )}
            </AccordionDetails>
          </Accordion>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" disabled={loading}>
          {loading ? <CircularProgress size={24} /> : editingSource ? 'Update' : 'Add'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
